import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbUpOutlinedIcon from '@material-ui/icons/ThumbUpOutlined';
import { getBlogLike, getBlogLikeOperation, getBlogSessionToken, setBlogLike } from '../../../Apis/touzenBlogApi';

const useStyles = makeStyles((theme) => ({
  root: { position: 'relative', margin: theme.spacing(3, 0), fontSize: '0.875rem', textAlign: 'left' },
  compact: { margin: 0 },
  actions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' },
  button: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: 44, minHeight: 44,
    padding: '8px', background: 'transparent', border: 0, borderRadius: 4, fontFamily: 'inherit', fontSize: '0.875rem', color: '#6F6F6F', cursor: 'pointer',
    '&[aria-pressed="true"], &:hover, &:focus': { color: theme.palette.primary.main },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  icon: { fontSize: '20px' },
  iconFrame: { position: 'relative', display: 'inline-flex', flexShrink: 0, transformOrigin: '50% 85%', animationName: 'none' },
  celebrate: {
    animationName: '$thumbPop', animationDuration: '700ms', animationTimingFunction: 'cubic-bezier(0.2, 0.75, 0.3, 1)', animationIterationCount: 1,
    '&::before, &::after': { content: '""', position: 'absolute', top: 0, width: 5, height: 5, borderRadius: '50%', backgroundColor: 'currentColor', transform: 'scale(0)', animationDuration: '700ms', animationIterationCount: 1 },
    '&::before': { left: -4, animationName: '$sparkLeft' },
    '&::after': { right: -4, animationName: '$sparkRight' },
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none', transform: 'none', '&::before, &::after': { content: 'none', animationName: 'none' } },
  },
  '@keyframes thumbPop': {
    '0%, 100%': { transform: 'translateY(0) rotate(0) scale(1)' },
    '20%': { transform: 'translateY(-12px) rotate(-14deg) scale(1.65)' },
    '40%': { transform: 'translateY(-18px) rotate(12deg) scale(1.9)' },
    '65%': { transform: 'translateY(-5px) rotate(-6deg) scale(1.3)' },
    '82%': { transform: 'translateY(0) rotate(3deg) scale(1.1)' },
  },
  '@keyframes sparkLeft': { '0%, 100%': { transform: 'translate(0, 0) scale(0)' }, '40%': { transform: 'translate(-10px, -14px) scale(1.2)' }, '70%': { transform: 'translate(-14px, -18px) scale(0.6)' } },
  '@keyframes sparkRight': { '0%, 100%': { transform: 'translate(0, 0) scale(0)' }, '40%': { transform: 'translate(10px, -14px) scale(1.2)' }, '70%': { transform: 'translate(14px, -18px) scale(0.6)' } },
  message: { fontSize: '0.8125rem', lineHeight: 1.7, marginTop: theme.spacing(1) },
  srStatus: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0 },
}));

const emptyState = { phase: 'loading', count: null, liked: null, actor: null, raw: null, message: 'いいねを確認しています。' };
const localFailure = (kind) => Object.assign(new Error('TOUZEN_BLOG_LIKE_STATE_FAILED'), { kind });
const storageKey = (slug) => `touzen.blog.like.v1:${encodeURIComponent(slug)}`;
const articleApi = { get: getBlogLike, set: setBlogLike, status: getBlogLikeOperation };
const operationId = (id) => typeof id === 'string' && id.length === 32 && /^[a-f0-9]{32}$/.test(id);
function readGuest(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return { raw, record: { liked: false, pending: null } };
    const record = JSON.parse(raw);
    const pending = record?.pending;
    if (!record || Object.keys(record).sort().join(',') !== 'liked,pending' || typeof record.liked !== 'boolean'
      || (pending !== null && (!pending || Object.keys(pending).sort().join(',') !== 'liked,operation_id,previously_liked'
        || !operationId(pending.operation_id) || typeof pending.liked !== 'boolean' || pending.previously_liked !== record.liked))) throw new Error();
    return { raw, record };
  } catch (error) { throw localFailure('storage'); }
}
function writeGuest(key, record) {
  try {
    const raw = JSON.stringify(record);
    window.localStorage.setItem(key, raw);
    if (window.localStorage.getItem(key) !== raw) throw new Error();
    return raw;
  } catch (error) { throw localFailure('storage'); }
}
function newOperationId() {
  try {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch (error) { throw localFailure('crypto'); }
}
function readMessage(error) {
  if (['unauthenticated', 'session_changed'].includes(error?.kind)) return 'ログイン状態を確認してから、状態を再読み込みしてください。';
  if (error?.kind === 'not_found') return 'この記事のいいねを利用できません。';
  if (error?.kind === 'storage') return 'ブラウザに状態を保存・確認できません。保存設定を確認して、状態を再読み込みしてください。';
  if (error?.kind === 'crypto') return 'このブラウザでは操作を安全に記録できません。いいねは送信していません。';
  if (error?.kind === 'storage_changed') return '別の画面で状態が変わりました。状態を再読み込みしてください。';
  return 'いいねの状態を読み込めませんでした。';
}

// The caller supplies the existing AuthContext scope; membership comes only from GET.
export default function BlogLikeButton({ slug, isAuthenticated = false, authScope = '', compact = false, articleTitle = '', api = articleApi, storageScope = '' }) {
  const classes = useStyles();
  const guestKey = storageScope ? `touzen.blog.comment-like.v1:${encodeURIComponent(storageScope)}` : storageKey(slug);
  const token = isAuthenticated ? getBlogSessionToken() : '';
  const [state, setState] = useState(emptyState);
  const [celebration, setCelebration] = useState(0);
  const celebrationId = useRef(0);
  const [, refreshToken] = useState(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  const mounted = useRef(false);
  const generation = useRef(0);
  const controller = useRef(null);
  const sending = useRef(false);
  const begin = useCallback(() => {
    controller.current?.abort();
    const next = new AbortController();
    controller.current = next;
    const requestGeneration = ++generation.current;
    sending.current = true;
    return { signal: next.signal, current: () => mounted.current && !next.signal.aborted && generation.current === requestGeneration };
  }, []);
  useLayoutEffect(() => {
    mounted.current = true;
    generation.current += 1;
    sending.current = false;
    celebrationId.current = 0;
    setCelebration(0);
    setState(emptyState);
    return () => { mounted.current = false; generation.current += 1; controller.current?.abort(); };
  }, [slug, authScope, isAuthenticated, token, api, guestKey]);

  const read = useCallback(async () => {
    if (isAuthenticated && getBlogSessionToken() !== token) { refreshToken((value) => value + 1); return; }
    const request = begin();
    let hasPending = false;
    setCelebration(0);
    setState((current) => ({ ...current, phase: 'loading', liked: null, message: 'いいねを確認しています。' }));
    try {
      const result = await api.get(slug, { authenticated: isAuthenticated, expectedToken: token, signal: request.signal });
      if (!request.current()) return;
      if (result.actor_kind === 'member') {
        setState({ phase: 'ready', count: result.count, liked: result.liked, actor: 'member', raw: null, message: '' });
        return;
      }
      setState((current) => ({ ...current, actor: 'guest' }));
      const guest = readGuest(guestKey);
      if (guest.record.pending) {
        hasPending = true;
        const operation = await api.status(slug, guest.record.pending.operation_id, { authenticated: isAuthenticated, expectedToken: token, signal: request.signal });
        if (!request.current()) return;
        if (readGuest(guestKey).raw !== guest.raw) throw localFailure('storage_changed');
        if (operation.operation_status !== 'confirmed') {
          setState({ phase: 'uncertain', count: operation.count, liked: null, actor: 'guest', raw: guest.raw, message: '結果を確認できません。再送せず、結果を再確認してください。' });
          return;
        }
        const raw = writeGuest(guestKey, { liked: operation.liked, pending: null });
        setState({ phase: 'ready', count: operation.count, liked: operation.liked, actor: 'guest', raw, message: 'いいねの結果を確認しました。' });
        return;
      }
      setState({ phase: 'ready', count: result.count, liked: guest.record.liked, actor: 'guest', raw: guest.raw, message: '' });
    } catch (error) {
      if (request.current()) setState((current) => ({ ...current, phase: hasPending ? 'uncertain' : 'read_error', liked: null,
        message: hasPending ? '結果を確認できません。再送せず、結果を再確認してください。' : readMessage(error) }));
    } finally { if (request.current()) sending.current = false; }
  }, [begin, slug, isAuthenticated, token, api, guestKey]);
  useEffect(() => { read(); }, [read, authScope]);
  useEffect(() => {
    const changed = (event) => {
      if (isAuthenticated && (event.key === 'accesstoken' || event.key === null)) {
        if (getBlogSessionToken() !== token) refreshToken((value) => value + 1);
        else read();
        return;
      }
      if (stateRef.current.actor === 'guest' && (event.key === guestKey || event.key === null)) read();
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [guestKey, isAuthenticated, token, read]);

  const toggle = async () => {
    if (sending.current || state.phase !== 'ready') return;
    if (isAuthenticated && getBlogSessionToken() !== token) { refreshToken((value) => value + 1); return; }
    setCelebration(0);
    let guest;
    let pending;
    try {
      if (state.actor === 'guest') {
        guest = readGuest(guestKey);
        if (guest.raw !== state.raw || guest.record.pending) { read(); return; }
        pending = { operation_id: newOperationId(), liked: !state.liked, previously_liked: state.liked };
        guest.raw = writeGuest(guestKey, { liked: state.liked, pending });
      }
    } catch (error) {
      setState((current) => ({ ...current, phase: 'read_error', liked: null, message: readMessage(error) }));
      return;
    }
    const request = begin();
    setState((current) => ({ ...current, phase: 'sending', message: 'いいねを反映しています。' }));
    try {
      const result = await api.set(slug, { actor_kind: state.actor, liked: !state.liked, ...(pending || {}) }, { authenticated: isAuthenticated, expectedToken: token, signal: request.signal });
      if (!request.current()) return;
      if (guest && readGuest(guestKey).raw !== guest.raw) throw localFailure('storage_changed');
      const raw = guest ? writeGuest(guestKey, { liked: result.liked, pending: null }) : null;
      if (state.liked === false && result.liked === true) {
        celebrationId.current += 1;
        setCelebration(celebrationId.current);
      }
      setState({ phase: 'ready', count: result.count, liked: result.liked, actor: result.actor_kind, raw,
        message: result.liked ? 'いいねを反映しました。' : 'いいねを取り消しました。' });
    } catch (error) {
      if (!request.current()) return;
      if ([400, 401, 403, 404].includes(error?.status)) {
        try {
          let raw = null;
          if (guest) {
            if (readGuest(guestKey).raw !== guest.raw) throw localFailure('storage_changed');
            raw = writeGuest(guestKey, { liked: pending.previously_liked, pending: null });
          }
          setState((current) => ({ ...current, phase: 'read_error', liked: null, raw,
            message: error.status === 401 ? readMessage(error) : 'いいねを受け付けられませんでした。状態を再読み込みしてください。' }));
        } catch (storageError) {
          setState((current) => ({ ...current, phase: 'read_error', liked: null, message: readMessage(storageError) }));
        }
      } else setState((current) => ({ ...current, phase: 'uncertain', liked: null, message: '結果を確認できません。再送せず、結果を再確認してください。' }));
    } finally { if (request.current()) sending.current = false; }
  };
  const uncertain = state.phase === 'uncertain';
  const Icon = state.liked === true ? ThumbUpIcon : ThumbUpOutlinedIcon;
  const label = state.liked ? 'いいねを取り消す' : 'いいね';
  const visibleMessage = uncertain || state.phase === 'read_error';
  return <div data-testid="blog-like" className={`${classes.root}${compact ? ` ${classes.compact}` : ''}`}>
    <div className={classes.actions}>
      <button type="button" className={classes.button} disabled={state.phase !== 'ready'} onClick={toggle}
        aria-pressed={state.liked === null ? undefined : state.liked} aria-label={articleTitle ? `${articleTitle}：${label}` : label}>
        <span key={celebration} data-testid="blog-like-feedback" aria-hidden="true" className={`${classes.iconFrame}${celebration ? ` ${classes.celebrate}` : ''}`}
          onAnimationEnd={(event) => { if (event.target === event.currentTarget && !event.nativeEvent.pseudoElement) setCelebration(0); }}>
          <Icon className={classes.icon} aria-hidden="true" focusable="false" />
        </span><span aria-label="いいねの件数">{uncertain ? '—' : state.count ?? '—'}</span>
      </button>
      {(uncertain || state.phase === 'read_error') && <button type="button" className={classes.button} onClick={read}>{uncertain ? '結果を再確認' : '状態を再読み込み'}</button>}
    </div>
    {state.message && <Typography role="status" aria-live="polite" aria-atomic="true" className={visibleMessage ? classes.message : classes.srStatus}>{state.message}</Typography>}
  </div>;
}
